import React from 'react';

import AdminLayout from 'Pages/Admin/AdminLayout';

import {PrimaryButton} from 'Components/Button';
import {Alert, Loading, Pagination} from 'Components/Partials';
import {Input, Select, Toggle} from 'Components/Form';
import {TickTable, TickHeader, TickRow, TickTextCell, TickCell} from 'Components/Services/TickTable';

import User from 'Services/User';
import AssignmentsApi from 'Services/Api/Admin/Services/Assignments';

export default class Assignments extends React.Component {
    /**
     * @var groups
     * @type {array}
     */
    groups = [
        {label: 'Staff and service users', value: 'all'},
        {label: 'Staff', value: 'Staff'},
        {label: 'Service users', value: 'Service User'},
    ];

    /**
     * @var types
     * @type {array}
     */
    types = [
        {label: 'People and houses', value: 'all'},
        {label: 'People only', value: 'person'},
        {label: 'Houses only', value: 'property'},
    ];

    /**
     * @var state
     */
    state = {
        loading: true,
        working: false,
        alert: null,
        users: null,
        changes: {},
        filters: {
            search: '',
            group: 'all',
            type: 'all',
            missing: false,
        },
        page: 1,
    };

    /**
     * @method componentDidMount
     */
    componentDidMount = () => {
        this.loadUsers();
    };

    /**
     * @method services
     * @return {array}
     */
    get services() {
        return User.data?.service_access?.services ?? [];
    }

    /**
     * @method loadUsers
     */
    loadUsers = async () => {
        const {filters, page} = this.state;

        this.setState({loading: true});

        const response = await AssignmentsApi.get(null, {
            search: filters.search || null,
            group: filters.group === 'all' ? null : filters.group,
            type: filters.type === 'all' ? null : filters.type,
            missing: filters.missing ? 'true' : null,
            page,
        });

        if (response.success) {
            this.setState({loading: false, users: response.data});
        }
    };

    /**
     * @method handleFilter
     * @param {string} key
     * @param {*} value
     */
    handleFilter = (key, value) => {
        this.setState({
            filters: {...this.state.filters, [key]: value},
            page: 1,
        }, key === 'search' ? this.debouncedLoad : this.loadUsers);
    };

    /**
     * @method debouncedLoad
     */
    debouncedLoad = () => {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(this.loadUsers, 400);
    };

    /**
     * @method goToPage
     * @param {number} page
     */
    goToPage = (page) => {
        this.setState({page}, this.loadUsers);
    };

    /**
     * Current values for a row, including unsaved changes.
     *
     * @method rowValues
     * @param {object} user
     * @return {object}
     */
    rowValues = (user) => {
        return this.state.changes[user.id] ?? {
            user_id: user.id,
            service_ids: user.service_ids,
            manager_service_ids: user.manager_service_ids,
        };
    };

    /**
     * @method toggle
     * @param {object} user
     * @param {string} key
     * @param {string} service_id
     */
    toggle = (user, key, service_id) => {
        const row = {...this.rowValues(user)};
        const ids = row[key];

        row[key] = ids.includes(service_id) ? ids.filter(id => id !== service_id) : [...ids, service_id];

        // A house belongs to exactly one service, so ticking one unticks the others.
        if (key === 'service_ids' && user.is_property && row.service_ids.includes(service_id)) {
            row.service_ids = [service_id];
        }

        // Managers must be assigned to the service they manage.
        row.manager_service_ids = row.manager_service_ids.filter(id => row.service_ids.includes(id));

        this.setState({changes: {...this.state.changes, [user.id]: row}});
    };

    /**
     * @method handleSave
     */
    handleSave = async () => {
        const assignments = Object.values(this.state.changes);

        this.setState({working: true, alert: null});

        const response = await AssignmentsApi.save(assignments);

        if (response.success) {
            this.setState({
                working: false,
                changes: {},
                alert: {type: 'success', message: `Saved ${assignments.length} change${assignments.length === 1 ? '' : 's'}.`},
            }, this.loadUsers);
        } else {
            this.setState({
                working: false,
                alert: {type: 'error', message: response.message, errors: response.errors},
            });
        }
    };

    /**
     * @method render
     * @return {JSX.Element}
     */
    render() {
        const {loading, working, alert, users, filters, page, changes} = this.state;
        const isAdmin = User.isAdmin;
        const changeCount = Object.keys(changes).length;

        return (
            <AdminLayout title="Service Assignments">
                <div className="p-6 space-y-6">
                    <p className="text-sm">
                        Tick the services each person works in or receives care from. A house belongs to exactly one
                        service. {isAdmin && 'Tick a service for a staff member, then press Manager if they manage it. Managers see all services and can assign services to others.'}
                    </p>

                    {alert !== null && (<Alert {...alert} />)}

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                        <Input
                            label="Search"
                            id="search"
                            value={filters.search}
                            onChange={(v) => this.handleFilter('search', v)}
                        />
                        <Select
                            label="Group"
                            id="group"
                            value={filters.group}
                            options={this.groups}
                            onChange={(v) => this.handleFilter('group', v)}
                        />
                        <Select
                            label="Type"
                            id="type"
                            value={filters.type}
                            options={this.types}
                            onChange={(v) => this.handleFilter('type', v)}
                        />
                        <Toggle
                            label="Missing a service only"
                            id="missing"
                            value={filters.missing}
                            onChange={(v) => this.handleFilter('missing', v)}
                        />
                    </div>

                    {loading && (<Loading/>)}

                    {!loading && users && (
                        <div>
                            <TickTable
                                head={
                                    <tr>
                                        <TickHeader>Name</TickHeader>
                                        <TickHeader>Group</TickHeader>
                                        {this.services.map(service => (
                                            <TickHeader key={service.id} tick>
                                                {service.name}
                                            </TickHeader>
                                        ))}
                                    </tr>
                                }
                            >
                                {users.data.length === 0 && (
                                    <tr><td className="py-4 px-3" colSpan={2 + this.services.length}>No matching people.</td></tr>
                                )}
                                {users.data.map((user, index) => {
                                    const row = this.rowValues(user);
                                    const canManage = isAdmin && !user.is_property && user.group === 'Staff';

                                    return (
                                        <TickRow key={user.id} index={index} changed={!!changes[user.id]}>
                                            <TickTextCell className="font-bold">{user.full_name}</TickTextCell>
                                            <TickTextCell>{user.is_property ? 'House' : user.group}</TickTextCell>
                                            {this.services.map(service => {
                                                const works = row.service_ids.includes(service.id);
                                                const manages = row.manager_service_ids.includes(service.id);

                                                return (
                                                    <TickCell
                                                        key={service.id}
                                                        label={`${user.full_name}: ${service.name}`}
                                                        checked={works}
                                                        onChange={() => this.toggle(user, 'service_ids', service.id)}
                                                    >
                                                        {canManage && works && (
                                                            <button
                                                                type="button"
                                                                aria-pressed={manages}
                                                                aria-label={`${user.full_name}: manager of ${service.name}`}
                                                                onClick={() => this.toggle(user, 'manager_service_ids', service.id)}
                                                                className={`text-xs font-bold rounded-full px-2.5 py-0.5 border
                                                                    ${manages ? 'bg-app-leading border-app-leading text-white' : 'bg-white border-gray-300 text-gray-600 hover:border-app-leading'}`}
                                                            >
                                                                {manages ? '★ Manager' : 'Manager'}
                                                            </button>
                                                        )}
                                                    </TickCell>
                                                );
                                            })}
                                        </TickRow>
                                    );
                                })}
                            </TickTable>

                            {users.meta.last_page > 1 && (
                                <Pagination
                                    className="mt-4"
                                    page={page}
                                    goToPage={this.goToPage}
                                    pageCount={users.meta.last_page}
                                />
                            )}
                        </div>
                    )}

                    <div className="flex items-center gap-4">
                        <PrimaryButton
                            text={working ? 'Saving...' : `Save${changeCount ? ` (${changeCount})` : ''}`}
                            disabled={working || changeCount === 0}
                            onClick={this.handleSave}
                        />
                        {changeCount > 0 && (
                            <span className="text-sm">Unsaved changes are kept when you change page.</span>
                        )}
                    </div>
                </div>
            </AdminLayout>
        );
    }
}
